import { Form, Input, Select } from "antd";
import React from "react";
import { ProjectsStore } from "../../../modules/common/stores";
import { InboxOutlined } from "@ant-design/icons";
import { Upload } from "antd";
import { useStore } from "../../common/stores/context";
import { safeStringLocaleCompare } from "../../common/misc/Utils";
import AppModal from "../../common/components/AppModal";

type Props = {
    projectsStore: ProjectsStore;
    isDialogVisible: boolean;
    closeDialog: () => void;
};
const FormItem = Form.Item;

const ImportProjectDialog: React.FC<Props> = ({
    projectsStore,
    isDialogVisible,
    closeDialog,
}) => {
    const { Dragger } = Upload;
    const { userProfileStore } = useStore();
    const [form] = Form.useForm();
    const [isLoading, setIsloading] = React.useState(false);
    const currentUserId = userProfileStore.currentUserId;

    const ownerOptions = React.useMemo(
        () =>
            projectsStore.allUsers
                .filter((u) => !u.isDeleted)
                .map((u) => ({
                    value: u.userId,
                    label: `${u.firstName} ${u.lastName}`.trim(),
                }))
                .sort((a, b) => safeStringLocaleCompare(b.label, a.label)),
        [projectsStore.allUsers],
    );

    React.useEffect(() => {
        if (
            isDialogVisible &&
            currentUserId &&
            form.getFieldValue("projectOwner") === undefined
        ) {
            form.setFieldsValue({ projectOwner: currentUserId });
        }
    }, [currentUserId, form, isDialogVisible]);

    const handleSubmit = () => {
        setIsloading(true);
        form.validateFields()
            .then(async (values) => {
                const result = await projectsStore.importProject(values);

                if (result.isOk()) {
                    form.resetFields();
                    closeDialog();
                }
                setIsloading(false);
            })
            .catch((err) => {
                setIsloading(false);
                console.log(err);
            });
    };

    const handleCancel = () => {
        form.resetFields();
        closeDialog();
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const projectsValidator = (rule: any, value: any, callback: any) => {
        if (!value) {
            callback();
        }
        const projectsToValidate = projectsStore!.projects.map((p) =>
            p.name.toLowerCase(),
        );
        if (
            value &&
            projectsToValidate.includes(value.toLowerCase()) &&
            projectsStore.editableProjectName !== value
        ) {
            callback("Project with the same name already exists");
        } else if (value?.length < 3) {
            callback("Project name must be at least 3 characters");
        } else {
            callback();
        }
    };

    return (
        <AppModal
            centered
            title="Import project"
            destroyOnClose
            closable={false}
            maskClosable={false}
            open={isDialogVisible}
            className="alpha-modal"
            cancelButtonTitle="Cancel"
            onCancelClick={handleCancel}
            cancelButtonProps={{
                "data-id": "import-project-cancel",
            }}
            submitButtonTitle="Import"
            onSubmitClick={handleSubmit}
            submitButtonProps={{
                "data-id": "import-project-submit",
                form: "add-project-form",
                loading: isLoading,
            }}
        >
            <Form
                className="inputs-container"
                style={{ marginTop: 10 }}
                id="new-task-template-form"
                layout="vertical"
                form={form}
            >
                <FormItem
                    name="file"
                    valuePropName="file"
                    colon={false}
                    className="dialog-field"
                    label={
                        <span className="dialog-field-label">Project file</span>
                    }
                    rules={[
                        { required: true, message: "Project file is required" },
                    ]}
                    getValueFromEvent={(e) => {
                        if (!e.file || !e.fileList || !e.fileList.length) {
                            return;
                        }

                        return e.file;
                    }}
                >
                    <Dragger
                        name="file"
                        beforeUpload={() => false}
                        maxCount={1}
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">
                            Click or drag a file to this area to upload
                        </p>
                    </Dragger>
                </FormItem>
                <FormItem
                    name="name"
                    colon={false}
                    className="dialog-field"
                    label={
                        <span className="dialog-field-label">Project name</span>
                    }
                    rules={[
                        { required: true, message: "Project name is required" },
                        { validator: projectsValidator },
                    ]}
                >
                    <Input data-id="input-Name" />
                </FormItem>
                <FormItem
                    name="projectOwner"
                    colon={false}
                    className="dialog-field"
                    label={
                        <span className="dialog-field-label">
                            Project owner
                        </span>
                    }
                >
                    <Select
                        allowClear
                        showSearch
                        placeholder="Unassigned"
                        style={{ width: "100%" }}
                        optionFilterProp="label"
                        options={ownerOptions}
                    />
                </FormItem>
            </Form>
        </AppModal>
    );
};

export default ImportProjectDialog;
